import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Assumptions, DocTypes } from '../static/Assumptions';
import { Policy } from './Policy';
import { FollowUpQA } from './FollowUpQA';
import { POLICY_DOC_DOCTYPE, POLICY_DOC_SCAN_STATUS } from '../constants/policy-doc';
import { MIMEFileType, MIMEFileTypes } from '../static/MIMEFileType';
import { Utils } from '../Utils';
import { Body } from 'aws-sdk/clients/s3';

@supertypeClass({ toClient: true, toServer: false })
export class PolicyDoc extends Everything {
  static DOCTYPE = POLICY_DOC_DOCTYPE;

  @property({
    toClient: false,
    toServer: false,
    sensitiveData: true,
    comment: 'actually buffer of the document',
  })
  doc: string;

  @property({
    comment: 'type of the document in terms of format, there are pdf and html',
  })
  type: string;

  @property({
    comment:
      'type of the document in terms of app type, values: docTypeappPacket, I-797, Amendment, Application, A2200, EIR, HIPPA, Lab results, Other, Financial Form, Rx, App, Packet, Tax Form, APS, Follow Up, RevisedBasicIllustration, Visa',
  })
  docType: string;

  @property({
    comment: 'Where the doc generated from. value: app, algo, uploaded',
  })
  docSource: string;

  // @NOTE value stored in db is not the same as values found in Assumptions.policyDocTypes
  @property({ values: Assumptions.policyDocTypes })
  appType: string;

  @property()
  subType: string;

  @property({
    getType: () => Policy,
    comment: 'foreign key for policy table which contains all policy related information',
  })
  policy: Policy;

  @property({
    comment: 'Name of document, such as MVR report, TLIC Underwriting Report, Labs Image, AppPacket, etc',
  })
  name: string;

  @property({
    comment: 'default is english',
  })
  language: string = 'en';

  @property({
    comment: 'date when this document was generated/uploaded',
  })
  date: Date;

  @property({
    comment:
      'Party or person updated the document, could be Workflow, or specific person, if it is null, that means it is generated by the app',
  })
  uploadedBy: string;

  @property({ getType: () => FollowUpQA })
  followUpQA: FollowUpQA;

  @property({
    getType: () => String,
    comment:
      'Party or person updated the document, could be Workflow, or specific person, if it is null, that means it is generated by the app',
  })
  roles: Array<string>;

  @property()
  order: number;

  @property({
    comment: 'Status of document scan in S3 - PENDING, CLEAN, INFECTED',
  })
  status: POLICY_DOC_SCAN_STATUS;

  @property({ isLocal: true, persist: false })
  friendlyName: string;

  @property({
    comment: 'Is document active or was it deleted',
  })
  isActive: boolean = true;

  @property({
    sensitiveData: true,
    isRemoteObject: true,
    remoteKeyBase: 'policy-doc-buffer',
    comment: 'policy document name which is in s3, will contain the actual Buffer data on persistor get/set',
  })
  docS3: string | Buffer;

  @property({
    comment: 'contains the type of document we are pushing ot s3',
  })
  docS3_RemoteMIMEType: MIMEFileType;

  constructor() {
    super();
  }

  static new(
    name: string,
    doc: Body,
    type: string,
    docSource: string,
    policy: Policy,
    appType: string,
    docType?: string,
    uploadedBy?: string,
    language?: string,
    roles?: string[],
    order?: number,
    status?: POLICY_DOC_SCAN_STATUS,
    mimeType?: MIMEFileType
  ) {
    return new PolicyDoc().init(
      name,
      doc,
      type,
      docSource,
      policy,
      appType,
      docType,
      uploadedBy,
      language,
      roles,
      order,
      status,
      mimeType
    );
  }

  init(
    name: string,
    doc,
    type: string,
    docSource: string,
    policy: Policy,
    appType: string,
    docType: string,
    uploadedBy: string,
    language: string,
    roles: string[],
    order: number,
    status: POLICY_DOC_SCAN_STATUS,
    mimeType: MIMEFileType
  ) {
    this.name = name || this.__values__('appType')[appType];
    this.type = type;
    this.docSource = docSource;
    this.policy = policy;
    this.date = new Date();
    this.docSource = docSource || 'uploaded';
    this.appType = appType || null;
    this.docType = docType || null;
    this.uploadedBy = uploadedBy || null;
    this.language = language || 'en';
    this.roles = roles;
    this.order = order;
    this.status = status; //Virus scan status
    this.docS3_RemoteMIMEType = mimeType || this.getMIMEFileType();
    this.docContent = doc;
    return this;
  }

  clone(newPolicy: Policy = null): PolicyDoc {
    const cloned = PolicyDoc.new(
      this.name,
      this.docContent,
      this.type,
      this.docSource,
      newPolicy,
      this.appType,
      this.docType,
      this.uploadedBy,
      this.language,
      this.roles,
      this.order,
      this.status
    );

    cloned.date = this.date;
    return cloned;
  }

  /**
   * Human readable name that should be used when document is downloaded from the browser
   * [policyNumber]_Application_Packet.pdf
   * [policyNumber]_Basic_Illustration.pdf
   */
  setFriendlyName(policyNumber: string): void {
    let name: string;
    switch (this.appType) {
      case POLICY_DOC_DOCTYPE.RevisedBasicIllustration:
        name = 'Revised_Basic_Illustration';
        break;
      case POLICY_DOC_DOCTYPE.basicIllustration:
        name = 'Basic_Illustration';
        break;
      case POLICY_DOC_DOCTYPE.annuityApplication:
        name = 'Annuity_Application';
        break;
      case POLICY_DOC_DOCTYPE.annuitySuitabilityForm:
        name = 'Annuity_Suitability_Form';
        break;
      case POLICY_DOC_DOCTYPE.annuityReplacementForm:
        name = 'Annuity_Replacement_Form';
        break;
      case POLICY_DOC_DOCTYPE.annuityCertificateOfTrustForm:
        name = 'Annuity_Certificate_Of_Trust_Form';
        break;
      case POLICY_DOC_DOCTYPE.annuityIDVerificationConsent:
        name = 'Annuity_ID_Verification_Consent_Form';
        break;
      case POLICY_DOC_DOCTYPE.annuityProductDisclosure:
        name = 'Annuity_Product_Disclosure';
        break;
      // @NOTE we cannot use Assumptions.policyDocTypes.Packet because function that save the document, don't use Assumptions.policyDocTypes
      case 'Packet':
      case 'AppPdf':
        name = 'Application_Packet';
        break;

      default:
        name = this._id;
        break;
    }

    this.friendlyName = `${policyNumber}_${name}`;
  }

  get fileNameWithExtension() {
    return `${this.friendlyName || this.name}.${this.type}`;
  }

  get docContent() {
    if (this.docS3) {
      return Buffer.isBuffer(this.docS3) && !this.docS3_RemoteMIMEType ? this.docS3.toString() : this.docS3;
    }

    return this.doc;
  }

  set docContent(content) {
    if (this.shouldBeUploadedToS3()) {
      this.docS3 = content;
      if (DocTypes.CustomerUpload !== this.appType) {
        this.status = POLICY_DOC_SCAN_STATUS.CLEAN;
      }
    } else {
      this.doc = content && content.toString();
    }
  }

  public shouldBeUploadedToS3(): boolean {
    const disablePolicyDocMigrationToS3 = this.amorphic.config.nconf.get('disablePolicyDocMigrationToS3');
    const localStackEnabled = this.amorphic.config.nconf.get('LOCALSTACK_ENABLED');
    return disablePolicyDocMigrationToS3 === 'false' && (!Utils.isTestingEnv() || localStackEnabled === 'true');
  }

  getMIMEFileType(): MIMEFileType {
    return this.docS3_RemoteMIMEType || MIMEFileTypes.findMimeCodeByExtension(this.type);
  }
}
